import { queryKeys } from '../../../../config/queryKeys'
import { usePagedList, type PagedList } from '../../../../hooks/usePagedList'
import { getOrgRegistrations } from '../../../../services/organization/orgRegistrationsService'
import type { OrgRegistration, OrgRegistrationFilters } from '../types'

/**
 * Long enough that flipping back to a tab or filter just viewed doesn't
 * refetch; short enough that spots left stay close to current.
 */
const STALE_TIME = 60 * 1000

/**
 * The Registrations tab's list for an organization and its sub-organizations,
 * paged as the user scrolls. Keyed per org AND filter set, so each tab and
 * filter combination keeps its own pages.
 */
export const useOrgRegistrations = (
  orgId: string | undefined,
  filters: OrgRegistrationFilters
): PagedList<OrgRegistration> => {
  'use memo'

  return usePagedList<OrgRegistration>({
    queryKey: queryKeys.orgRegistrations.list(orgId, filters),
    fetchPage: (page) =>
      getOrgRegistrations({ orgId: orgId as string, filters, page }),
    enabled: Boolean(orgId),
    staleTime: STALE_TIME,
    // Registrations are edited in Registration Setup, often in another browser
    // tab whose cache invalidation never reaches this one. On returning to the
    // window, refetch if the list is past STALE_TIME; otherwise serve the cache.
    refetchOnWindowFocus: true,
  })
}
